import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PortfolioItem.css';

interface PortfolioItemProps {
  id: string;
  title: string;
  subtitle: string;
  image: string;
  video: string;
  isFirst?: boolean; // 添加标识是否为第一个item
  index?: number; // 添加索引用于计算延迟
}

const PortfolioItem: React.FC<PortfolioItemProps> = ({ id, title, subtitle, image, video, isFirst = false, index = 0 }) => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [showImageLoaded, setShowImageLoaded] = useState(false);
  const [showVideoLoaded, setShowVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  
  // 移动端检测和自动播放状态
  const [isMobile] = useState(() => window.innerWidth <= 768);
  const [isInView, setIsInView] = useState(false);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);

  // 延迟渲染逻辑 - 移除，直接渲染所有内容
  // useEffect(() => {
  //   const delay = index * 150;
  //   const timer = setTimeout(() => {
  //     console.log('ssssssss=========',delay)
  //     setShouldRender(true);
  //   }, delay);

  //   return () => clearTimeout(timer);
  // }, [index]);

  // 图片加载完成后延迟显示image-loaded class
  useEffect(() => {
    if (isImageLoaded) {
      const delay = index * 150;
      const timer = setTimeout(() => {
        setShowImageLoaded(true);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [isImageLoaded, index]);

  // 视频加载完成后延迟显示image-loaded class
  useEffect(() => {
    if (isVideoLoaded) {
      const delay = index * 150;
      const timer = setTimeout(() => {
        setShowVideoLoaded(true);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [isVideoLoaded, index]);

  // 预加载视频（保持原有逻辑）
  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement && video) {
      videoElement.load();

      const handleCanPlay = () => {
        // 移动端第一个视频加载完成后自动播放
        if (isMobile && isFirst && !hasAutoPlayed) {
          setTimeout(() => {
            videoElement.play().catch((error) => {
              console.log('First video auto-play failed, forcing state refresh:', error);
              
              // 播放失败后，延迟刷新状态模拟页面返回效果
              setTimeout(() => {
                // 强制重新触发播放
                setHasAutoPlayed(false);
                setTimeout(() => {
                  videoElement.play().catch(() => {
                    console.log('Retry play failed');
                  });
                  setHasAutoPlayed(true);
                }, 100);
              }, 1000);
            });
            setHasAutoPlayed(true);
          }, 1500); // 增加延迟到1500ms
        }
      };

      videoElement.addEventListener('canplay', handleCanPlay);

      return () => {
        videoElement.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, [video, isMobile, isFirst, hasAutoPlayed]);

  // 移动端自动播放逻辑（不影响桌面端hover）
  useEffect(() => {
    if (!isMobile || !video || !videoRef.current || !itemRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // 移动端垂直滚动，检测垂直方向的可见性
        const rect = entry.boundingClientRect;
        const windowHeight = window.innerHeight;
        
        // 计算元素在屏幕中的位置
        const elementCenter = rect.top + rect.height / 2;
        const screenCenter = windowHeight / 2;
        
        // 判断元素是否在屏幕中央区域（上下各留20%边距）
        const isInCenterArea = elementCenter > windowHeight * 0.2 && elementCenter < windowHeight * 0.8;
        const isVisible = entry.isIntersecting && isInCenterArea;
        
        setIsInView(isVisible);
        
        if (isVisible && videoRef.current) {
          console.log('Mobile auto-play video for:', title); // 调试日志
          videoRef.current.play().catch((error) => {
            console.log('Video play failed:', error); // 调试日志
          });
        } else if (videoRef.current) {
          videoRef.current.pause();
        }
      },
      {
        threshold: [0.1, 0.5, 0.9],
        rootMargin: '0px'
      }
    );

    observer.observe(itemRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isMobile, video, title]);

  // 桌面端hover逻辑（保持完全不变）
  const handleMouseEnter = () => {
    if (videoRef.current && video) {
      videoRef.current.play().catch(console.error);
    }
  };

 
  return (
    <Link to={`/product/${id}`} className="portfolio-item-link">
      <div
        ref={itemRef}
        className={`portfolio-item ${isFirst && isMobile ? 'first-item-mobile' : ''}`}
        onMouseEnter={handleMouseEnter}
      >
        <div className="portfolio-item-media loaded">
          {/* 静态图片 */}
          <img
            src={image}
            alt={title}
            className={`portfolio-image ${showImageLoaded ? 'image-loaded' : ''}`}
            loading="eager"
            referrerPolicy="no-referrer"
            onLoad={() => setIsImageLoaded(true)}
          />

          {/* 视频 */}
          {video && (
            <video
              ref={videoRef}
              className={`portfolio-video ${showVideoLoaded ? 'image-loaded' : ''}`}
              muted
              playsInline
              preload="metadata"
              onLoadedMetadata={() => setIsVideoLoaded(true)}
            >
              <source src={video} type="video/mp4" />
            </video>
          )}

          {/* 标题覆盖层 */}
          <div className="portfolio-overlay">
            <h3 className="portfolio-title">{title}</h3>
            <div className="view-detail">{subtitle}</div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default PortfolioItem;